/**
 * Error Context Provider
 * Manages application-wide error state and provides error handling utilities
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface AppError {
  id: string;
  message: string;
  code?: string;
  statusCode?: number;
  details?: Record<string, unknown>;
  recoverySuggestions?: string[];
  timestamp: Date;
  context?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  persistent?: boolean;
}

interface ErrorContextType {
  errors: AppError[];
  addError: (error: Omit<AppError, 'id' | 'timestamp'>) => string;
  removeError: (id: string) => void;
  clearErrors: () => void;
  clearErrorsByContext: (context: string) => void;
  getErrorsByContext: (context: string) => AppError[];
  hasErrors: boolean;
  hasCriticalErrors: boolean;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

interface ErrorProviderProps {
  children: ReactNode;
  maxErrors?: number;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ 
  children, 
  maxErrors = 50 
}) => {
  const [errors, setErrors] = useState<AppError[]>([]);

  const addError = useCallback((errorData: Omit<AppError, 'id' | 'timestamp'>): string => {
    const error: AppError = {
      ...errorData,
      id: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date()
    };

    setErrors(currentErrors => {
      // Remove oldest errors if we exceed maxErrors
      let newErrors = [...currentErrors, error];
      if (newErrors.length > maxErrors) {
        newErrors = newErrors.slice(-maxErrors);
      }

      // Log error for debugging
      console.error('Application Error:', error);

      return newErrors;
    });

    return error.id;
  }, [maxErrors]);

  const removeError = useCallback((id: string) => {
    setErrors(currentErrors => currentErrors.filter(error => error.id !== id));
  }, []);

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  const clearErrorsByContext = useCallback((context: string) => {
    setErrors(currentErrors => 
      currentErrors.filter(error => error.context !== context)
    );
  }, []);

  const getErrorsByContext = useCallback((context: string): AppError[] => {
    return errors.filter(error => error.context === context);
  }, [errors]);

  const hasErrors = errors.length > 0;
  const hasCriticalErrors = errors.some(error => error.severity === 'critical');

  const value: ErrorContextType = {
    errors,
    addError,
    removeError,
    clearErrors,
    clearErrorsByContext,
    getErrorsByContext,
    hasErrors,
    hasCriticalErrors
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
};

export const useErrorContext = (): ErrorContextType => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useErrorContext must be used within an ErrorProvider');
  }
  return context;
};

/**
 * Hook for creating API error handlers
 */
export const useApiErrorHandler = (context: string = 'api') => {
  const { addError } = useErrorContext();

  const handleApiError = useCallback((error: unknown, operationContext?: string) => {
    let appError: Omit<AppError, 'id' | 'timestamp'>;

    if (error && typeof error === 'object' && 'message' in error) {
      // Structured API error
      const errorObj = error as Record<string, unknown>;
      appError = {
        message: (errorObj.message as string) || 'An API error occurred',
        code: (errorObj.code as string) || (errorObj.error_code as string) || 'API_ERROR',
        statusCode: (errorObj.statusCode as number) || (errorObj.status as number),
        details: (errorObj.details as Record<string, unknown>) || errorObj,
        recoverySuggestions: (errorObj.recoverySuggestions as string[]) || (errorObj.recovery_suggestions as string[]) || [
          'Check your internet connection',
          'Try again in a few moments',
          'Contact support if the problem persists'
        ],
        context: operationContext || context,
        severity: (((errorObj.statusCode as number) || 0) >= 500) ? 'high' : 'medium'
      };
    } else if (error instanceof Error) {
      // JavaScript Error
      appError = {
        message: error.message,
        code: 'JS_ERROR',
        details: { stack: error.stack },
        recoverySuggestions: ['Try refreshing the page', 'Clear your browser cache'],
        context: operationContext || context,
        severity: 'medium'
      };
    } else {
      // Unknown error type
      appError = {
        message: String(error) || 'An unknown error occurred',
        code: 'UNKNOWN_ERROR',
        details: error as Record<string, unknown>,
        recoverySuggestions: ['Try refreshing the page'],
        context: operationContext || context,
        severity: 'medium'
      };
    }

    return addError(appError);
  }, [addError, context]);

  return handleApiError;
};

/**
 * Hook for creating form validation error handlers
 */
export const useValidationErrorHandler = (context: string = 'validation') => {
  const { addError, clearErrorsByContext } = useErrorContext();

  const handleValidationErrors = useCallback((errors: string[], persistent: boolean = false) => {
    // Clear previous validation errors for this context
    clearErrorsByContext(context);

    // Add new validation errors
    errors.forEach(errorMessage => {
      addError({
        message: errorMessage,
        code: 'VALIDATION_ERROR',
        context,
        severity: 'low',
        persistent,
        recoverySuggestions: ['Please check your input and try again']
      });
    });
  }, [addError, clearErrorsByContext, context]);

  const clearValidationErrors = useCallback(() => {
    clearErrorsByContext(context);
  }, [clearErrorsByContext, context]);

  return {
    handleValidationErrors,
    clearValidationErrors
  };
};

/**
 * Hook for creating training-specific error handlers
 */
export const useTrainingErrorHandler = () => {
  const apiErrorHandler = useApiErrorHandler('training');
  const validationErrorHandler = useValidationErrorHandler('training_validation');

  const handleTrainingError = useCallback((error: unknown, phase?: string) => {
    const operationContext = phase ? `training_${phase}` : 'training';
    return apiErrorHandler(error, operationContext);
  }, [apiErrorHandler]);

  return {
    handleTrainingError,
    handleValidationErrors: validationErrorHandler.handleValidationErrors,
    clearValidationErrors: validationErrorHandler.clearValidationErrors
  };
};